import { Button, ButtonProps } from '@/components/atom/Button';
import { TransactionModal } from '@/components/modals/TransactionModal';
import { ExchangeActionParams } from '@/hooks/exchange/actions';
import { ActionParams, useActions } from '@/hooks/staking/actions';
import { useTranslation } from '@/hooks/support/translate';
import { useTransactions } from '@/hooks/transactions';
import { useWallet } from '@/hooks/wallet';
import { appModalManager, Events } from '@/state/app';
import { walletAtom } from '@/state/wallet';
import { getNetworkByChainId } from '@/utils/utils';
import { FC, useMemo, useState } from 'react';
import { useRecoilValue, useSetRecoilState } from 'recoil';

export type TransactionParams = {
    text: string;
    event?: Events;
    handler?: CallableFunction;
    validation?: CallableFunction;
    onSuccess?: () => void;
    isDisabled?: boolean;
    params?: ActionParams | ExchangeActionParams;
    showModal?: boolean;
    lockForm?: boolean;
    successMsg?: string;
    failedMsg?: string;
    uniqueId?: string;
};

type TransactionActionProps = {
    transaction: TransactionParams;
    targetChainId: number | null | undefined;
    buttonProps?: ButtonProps;
};

export const TransactionAction: FC<TransactionActionProps> = (props) => {
    const [state, setState] = useState({
        processing: false,
        modalVisible: false,
        txHash: '',
    });
    const { t } = useTranslation();
    const { switchNetwork } = useWallet();
    const { pushTx } = useTransactions();
    const { txSuccess } = useActions();
    const setModal = useSetRecoilState(appModalManager);
    const walletState = useRecoilValue(walletAtom);
    const network = getNetworkByChainId(props.targetChainId);
    const isPending = false;

    const handleTransaction = async (params: TransactionParams) => {
        setState((previous) => ({ ...previous, processing: true }));
        if (!params.validation || (await params.validation())) {
            const tx = params.handler ? await params.handler(params.params) : null;
            if (!!tx) {
                // Push transaction and stop button spinner
                pushTx({
                    hash: tx.hash,
                    chainId: walletState.chainId!,
                    address: walletState.selectedAddress!,
                    successMsg: params.successMsg,
                    failedMsg: params.failedMsg,
                    event: params.event!,
                    uniqueId: params.uniqueId,
                });

                setState((previous) => ({
                    ...previous,
                    processing: !!params.lockForm,
                    modalVisible: !!params.showModal,
                    txHash: tx.hash,
                }));

                params.onSuccess && params.onSuccess();

                // await locally if button locks
                if (params.lockForm) {
                    await txSuccess(tx);
                }
            }
        }
        setState((previous) => ({ ...previous, processing: false }));
    };

    const item = useMemo(() => {
        let text = props.transaction.text;
        let handleClick = async () => await handleTransaction(props.transaction);
        let isDisabled = props.transaction.isDisabled || props.buttonProps?.isDisabled;

        // Connect Wallet
        if (!walletState.selectedAddress) {
            text = t('common.connect');
            handleClick = async () => setModal('connect');
            isDisabled = false;
        }

        // Switch Network
        if (!!walletState.selectedAddress && props.targetChainId && props.targetChainId != walletState.chainId) {
            text = t('common.switch', { network: network.name });
            handleClick = async () => {
                await switchNetwork(props.targetChainId!);
            };
            isDisabled = false;
        }
        return {
            text,
            handleClick,
            isDisabled,
        };
    }, [
        props.transaction.params,
        props.transaction.event,
        props.transaction.text,
        props.transaction.isDisabled,
        props.buttonProps,
        props.targetChainId,
        walletState.selectedAddress,
        walletState.chainId,
    ]);

    return (
        <>
            <TransactionModal
                visible={state.modalVisible}
                txHash={state.txHash}
                onClose={() => setState((previous) => ({ ...previous, modalVisible: false }))}
            />
            <Button
                id="action-btn"
                name={item.text}
                size="block"
                onClick={item.handleClick}
                isLoading={state.processing || isPending}
                {...props.buttonProps}
                isDisabled={item.isDisabled || state.processing}
            >
                {item.text}
            </Button>
        </>
    );
};
